import { useEffect, useRef, useState } from 'react';
import { Search, Code2, BookOpen } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useProblems } from '@/lib/hooks/useProblems';
import { useTopicSearch, type TopicSearchResult } from '@/lib/hooks/useTopicSearch';
import type { Problem } from '@/lib/api/problems';
import { DifficultyTag, type Difficulty } from '@kodprep/platform-ui/atomic';

type ProblemItem = { kind: 'problem'; slug: string; title: string; difficulty: Difficulty };
type TopicItem = { kind: 'topic'; id: string; title: string; module_title: string; batch_id: string };
type ResultItem = ProblemItem | TopicItem;

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // ⌘K toggle
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);


  // Reset state on open
  useEffect(() => {
    if (open) {
      setRaw('');
      setQ('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Debounce raw → q
  useEffect(() => {
    const t = setTimeout(() => setQ(raw.trim()), 300);
    return () => clearTimeout(t);
  }, [raw]);

  const { data: problems, isFetching: loadingProblems } = useProblems({ q });
  const { data: topics, isFetching: loadingTopics } = useTopicSearch(q);

  // useProblems resolves to `{ problems, total, ... }`, but a no-match search can
  // return `problems: null` (Go marshals a nil slice to null). Coerce to an array
  // before slicing so an empty/null result never throws.
  const problemList: Problem[] = Array.isArray(problems)
    ? (problems as Problem[])
    : Array.isArray((problems as any)?.problems)
      ? (problems as any).problems
      : [];

  const problemItems: ProblemItem[] = q.length >= 2
    ? problemList.slice(0, 5).map((p: Problem) => ({
        kind: 'problem',
        slug: p.slug,
        title: p.title,
        difficulty: p.difficulty as Difficulty,
      }))
    : [];

  const topicItems: TopicItem[] = q.length >= 2
    ? (Array.isArray(topics) ? topics : []).slice(0, 8).map((t: TopicSearchResult) => ({
        kind: 'topic',
        id: t.id,
        title: t.title,
        module_title: t.module_title,
        batch_id: t.batch_id,
      }))
    : [];

  const allItems: ResultItem[] = [...problemItems, ...topicItems];

  const navigateTo = (item: ResultItem) => {
    if (item.kind === 'problem') {
      navigate({ to: '/problems/$slug', params: { slug: item.slug } });
    } else {
      navigate({ to: '/courses/$batchId', params: { batchId: item.batch_id } });
    }
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && allItems[cursor]) {
      navigateTo(allItems[cursor]);
    }
  };

  const isLoading = q.length >= 2 && (loadingProblems || loadingTopics);
  const hasResults = allItems.length > 0;
  const noResults = q.length >= 2 && !isLoading && !hasResults;

  // Clamp cursor when result count changes
  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(allItems.length - 1, 0)));
  }, [allItems.length]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-sm text-muted-foreground shadow-sm transition-colors hover:bg-muted/50 md:h-auto md:w-full md:justify-start md:gap-2.5 md:px-4 md:py-2"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden flex-1 text-left md:inline">Search anything...</span>
        <kbd className="hidden rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold md:inline">⌘ K</kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[15vh]"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-popover shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={raw}
                onChange={(e) => { setRaw(e.target.value); setCursor(0); }}
                onKeyDown={handleKeyDown}
                placeholder="Search problems, topics…"
                className="w-full bg-transparent py-3 text-sm outline-none"
              />
            </div>

            {q.length < 2 && (
              <div className="px-4 py-5">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Search for</p>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground">
                    <Code2 className="h-4 w-4 shrink-0" />
                    <span>Problems — by title or topic</span>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground">
                    <BookOpen className="h-4 w-4 shrink-0" />
                    <span>Course lessons from your batch</span>
                  </div>
                </div>
                <p className="mt-4 text-[11px] text-muted-foreground/60">Type at least 2 characters to search</p>
              </div>
            )}

            {isLoading && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground animate-pulse">
                Searching…
              </div>
            )}

            {noResults && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No results for &ldquo;{q}&rdquo;
              </div>
            )}

            {!isLoading && hasResults && (
              <div className="max-h-80 overflow-y-auto py-2">
                {problemItems.length > 0 && (
                  <Section label="Problems">
                    {problemItems.map((item, i) => {
                      return (
                        <ResultRow
                          key={item.slug}
                          active={cursor === i}
                          onMouseEnter={() => setCursor(i)}
                          onClick={() => navigateTo(item)}
                        >
                          <Code2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="flex-1 truncate text-sm">{item.title}</span>
                          <DifficultyTag difficulty={item.difficulty} />
                        </ResultRow>
                      );
                    })}
                  </Section>
                )}

                {topicItems.length > 0 && (
                  <Section label="Lessons">
                    {topicItems.map((item, i) => {
                      const idx = problemItems.length + i;
                      return (
                        <ResultRow
                          key={item.id}
                          active={cursor === idx}
                          onMouseEnter={() => setCursor(idx)}
                          onClick={() => navigateTo(item)}
                        >
                          <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm">{item.title}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.module_title}</p>
                          </div>
                        </ResultRow>
                      );
                    })}
                  </Section>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

function ResultRow({
  active,
  onClick,
  onMouseEnter,
  children,
}: {
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
        active ? 'bg-muted' : 'hover:bg-muted/50'
      }`}
    >
      {children}
    </button>
  );
}
